import { convertFromRaw } from 'draft-js';

export class WhatsappUtils {
  static draftBlockIsUnorderedListItem(block) {
    return String((block && block.type) || '').toLowerCase() === 'unordered-list-item'
  }

  static buildWaBodyCharsFromDraftRaw(raw) {
    const chars = []
    if (!raw) return chars
    let contentState
    try {
      contentState = convertFromRaw(raw)
    } catch (e) {
      return chars
    }
    const rawBlocks = raw.blocks || []
    const blocksInOrder = contentState.getBlocksAsArray()
    rawBlocks.forEach((rb, bi) => {
      if (bi > 0) chars.push({ ch: '\n', bold: false, italic: false })
      if (WhatsappUtils.draftBlockIsUnorderedListItem(rb)) {
        chars.push({ ch: '-', bold: false, italic: false })
        chars.push({ ch: ' ', bold: false, italic: false })
      }
      const draftBlock =
        rb.key != null && rb.key !== '' ? contentState.getBlockForKey(rb.key) : blocksInOrder[bi]
      if (!draftBlock) return
      const text = draftBlock.getText() || ''
      const charList = draftBlock.getCharacterList()
      const n = text.length
      for (let i = 0; i < n; i++) {
        const style = charList.get(i).getStyle()
        const bold = style.has('BOLD') || style.has('bold')
        const italic = style.has('ITALIC') || style.has('italic')
        chars.push({ ch: text[i], bold, italic })
      }
    })
    return chars
  }

  static normalizeWaVariableSpansInChars(chars) {
    const text = chars.map(c => c.ch).join('')
    const re = /\{\{\d+\}\}/g
    let m
    while ((m = re.exec(text)) !== null) {
      const start = m.index
      const end = start + m[0].length
      let anyB = false
      let anyI = false
      for (let i = start; i < end; i++) {
        if (chars[i].bold) anyB = true
        if (chars[i].italic) anyI = true
      }
      for (let i = start; i < end; i++) {
        const c = chars[i]
        chars[i] = { ch: c.ch, bold: anyB, italic: anyI }
      }
    }
  }

  static normalizeWaStyleWordBoundaries(chars) {
    // Do not treat `{` / `}` as boundaries: placeholders like `{{1}}` or `{{contact.name}}`
    // must keep the same bold/italic run across the whole token. Otherwise style gets stripped
    // from the braces and `waCharsToWhatsAppFormattedString` emits broken markers (e.g. `*Hi {{1*}},`).
    const isBoundaryChar = ch => /[\s.,!?;:()[\]"']/.test(ch)
    const hasTokenBoundaryBefore = (arr, idx) => idx <= 0 || isBoundaryChar(arr[idx - 1].ch)
    const hasTokenBoundaryAfter = (arr, idx) =>
      idx >= arr.length - 1 || isBoundaryChar(arr[idx + 1].ch)
    const isStyleAnchorStart = (arr, idx) => {
      if (idx < 0 || idx >= arr.length) return false
      return !isBoundaryChar(arr[idx].ch) && hasTokenBoundaryBefore(arr, idx)
    }
    const isStyleAnchorEnd = (arr, idx) => {
      if (idx < 0 || idx >= arr.length) return false
      return !isBoundaryChar(arr[idx].ch) && hasTokenBoundaryAfter(arr, idx)
    }
    const clearStyleAt = (arr, idx) => {
      const c = arr[idx]
      arr[idx] = { ch: c.ch, bold: false, italic: false }
    }
    const n = chars.length
    let i = 0
    while (i < n) {
      const runBold = !!chars[i].bold
      const runItalic = !!chars[i].italic
      let j = i + 1
      while (j < n && !!chars[j].bold === runBold && !!chars[j].italic === runItalic) j++
      if (runBold || runItalic) {
        let left = i
        let right = j - 1
        while (left <= right && !isStyleAnchorStart(chars, left)) {
          clearStyleAt(chars, left)
          left++
        }
        while (right >= left && !isStyleAnchorEnd(chars, right)) {
          clearStyleAt(chars, right)
          right--
        }
      }
      i = j
    }
  }

  static waCharsToWhatsAppFormattedString(chars) {
    if (!chars.length) return ''
    let out = ''
    let prevB = false
    let prevI = false
    const appendCloseMarker = marker => {
      if (!marker) return
      const trailingSpaces = out.match(/[ \t]+$/)
      if (trailingSpaces) {
        const ws = trailingSpaces[0]
        out = `${out.slice(0, out.length - ws.length)}${marker}${ws}`
        return
      }
      out += marker
    }
    const close = () => {
      if (prevB && prevI) appendCloseMarker('_*')
      else if (prevB) appendCloseMarker('*')
      else if (prevI) appendCloseMarker('_')
    }
    const open = (b, i) => {
      if (b && i) out += '*_'
      else if (b) out += '*'
      else if (i) out += '_'
    }
    for (let k = 0; k < chars.length; k++) {
      const b = chars[k].bold
      const it = chars[k].italic
      if (b !== prevB || it !== prevI) {
        close()
        open(b, it)
        prevB = b
        prevI = it
      }
      out += chars[k].ch
    }
    close()
    return out
  }

  static waRichTextToWhatsAppBodyText(richTextJson) {
    if (richTextJson == null || richTextJson === '') return ''
    let parsed
    try {
      parsed = typeof richTextJson === 'string' ? JSON.parse(richTextJson) : richTextJson
    } catch (e) {
      return ''
    }
    if (!parsed || !parsed.__azformatted) return ''
    const chars = WhatsappUtils.buildWaBodyCharsFromDraftRaw(parsed.__azformatted)
    if (chars.length === 0) return ''
    WhatsappUtils.normalizeWaVariableSpansInChars(chars)
    WhatsappUtils.normalizeWaStyleWordBoundaries(chars)
    const formatted = WhatsappUtils.waCharsToWhatsAppFormattedString(chars)
    return WhatsappUtils.normalizeWaTemplateVariableTokens(formatted)
  }

  static normalizeWaTemplateVariableTokens(text) {
    return String(text || '').replace(/\{\{([^{}]+)\}\}/g, (match, inner) => {
      const raw = String(inner || '').trim()
      const wrapped = raw.match(/^([*_~`]+)\s*([A-Za-z0-9_.]+)\s*([*_~`]+)$/)
      if (wrapped) {
        const left = wrapped[1] || ''
        const core = wrapped[2] || ''
        const right = wrapped[3] || ''
        if (core) return `${left}{{${core}}}${right}`
      }
      if (/^[A-Za-z0-9_.]+$/.test(raw)) return `{{${raw}}}`
      return match
    })
  }

  static safeStr(value) {
    if (value == null) return ''
    return String(value)
  }

  static upper(value) {
    return WhatsappUtils.safeStr(value).trim().toUpperCase()
  }

  static lower(value) {
    return WhatsappUtils.safeStr(value).trim().toLowerCase()
  }

  /**
   * Maps outbound send API `obPayload.template` components to variable values
   * for `buildPreviewComponentsFromTemplate`.
   */
  static obPayloadTemplateToVariableValues(template) {
    const vars = []
    const comps = Array.isArray(template && template.components) ? template.components : []
    comps.forEach((comp) => {
      if (!comp || typeof comp !== 'object') return
      const type = WhatsappUtils.upper(comp.type)
      const params = Array.isArray(comp.parameters) ? comp.parameters : []
      if (type === 'HEADER') {
        params.forEach((p, i) => {
          if (!p || typeof p !== 'object') return
          const pt = WhatsappUtils.lower(p.type)
          if (pt === 'text') {
            vars.push({
              type: 'HEADER',
              format: 'text',
              vIndex: i + 1,
              text: WhatsappUtils.safeStr(p.text).trim(),
            })
          } else if (['image', 'video', 'document'].includes(pt)) {
            const media = p[pt] || {}
            const row = {
              type: 'HEADER',
              format: pt,
              mediaLink: WhatsappUtils.safeStr(media.link).trim(),
            }
            if (pt === 'document') {
              row.fileName = WhatsappUtils.safeStr(media.filename).trim()
            }
            vars.push(row)
          }
        })
      } else if (type === 'BODY') {
        params.forEach((p, i) => {
          if (!p || WhatsappUtils.lower(p.type) !== 'text') return
          vars.push({
            type: 'BODY',
            format: 'text',
            vIndex: i + 1,
            text: WhatsappUtils.safeStr(p.text).trim(),
          })
        })
      } else if (type === 'BUTTON') {
        const subType = WhatsappUtils.lower(comp.sub_type)
        const buttonIndex = Number.isInteger(comp.index) ? comp.index : 0
        params.forEach((p, i) => {
          if (!p || WhatsappUtils.lower(p.type) !== 'text') return
          const text = WhatsappUtils.safeStr(p.text).trim()
          if (subType === 'url') {
            vars.push({
              type: 'URL',
              format: 'text',
              vIndex: i + 1,
              text,
              buttonId: String(buttonIndex),
            })
          } else {
            vars.push({
              type: 'BUTTON',
              format: 'text',
              vIndex: i + 1,
              text,
              buttonId: String(buttonIndex),
            })
          }
        })
      }
    })
    return vars
  }

  /** Maps outbound API `obPayload.contentBody` to WhatsappSessionPreview session fields. */
  static obPayloadContentBodyToSession(contentBody) {
    const cb = contentBody || {}
    const session = {
      headerType: 'NONE',
      headerText: '',
      mediaLink: '',
      fileName: '',
      bodyText: '',
      footerText: '',
      button1Type: 'NONE',
      button1Text: '',
      button1Url: '',
      button2Type: 'NONE',
      button2Text: '',
      button3Type: 'NONE',
      button3Text: '',
      sectionTitle: '',
    }

    const header = cb.header || (cb.interactive && cb.interactive.header)
    if (header && header.type) {
      const ht = WhatsappUtils.upper(header.type)
      if (ht === 'TEXT') {
        session.headerType = 'TEXT'
        session.headerText = WhatsappUtils.safeStr(header.text).trim()
      } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(ht)) {
        const formatKey = WhatsappUtils.lower(ht)
        let mediaLink = ''
        let fileName = ''
        const nested = header[formatKey]
        if (nested) {
          mediaLink = WhatsappUtils.safeStr(nested.link || nested.url).trim()
          if (ht === 'DOCUMENT') {
            fileName = WhatsappUtils.safeStr(nested.filename).trim()
          }
        }
        if (!mediaLink) {
          mediaLink = WhatsappUtils.safeStr(header.link || header.url).trim()
        }
        if (!mediaLink && Array.isArray(header.parameters)) {
          header.parameters.forEach((p) => {
            if (mediaLink || !p || typeof p !== 'object') return
            const pt = WhatsappUtils.lower(p.type)
            if (!['image', 'video', 'document'].includes(pt)) return
            const media = p[pt] || {}
            mediaLink = WhatsappUtils.safeStr(media.link || media.url).trim()
            if (mediaLink && ht === 'DOCUMENT') {
              fileName = WhatsappUtils.safeStr(media.filename).trim()
            }
          })
        }
        if (!mediaLink) {
          mediaLink = WhatsappUtils.safeStr(cb.mediaLink || cb.link).trim()
        }
        if (nested || mediaLink) {
          session.headerType = ht
          session.mediaLink = mediaLink
          if (ht === 'DOCUMENT') {
            session.fileName = fileName
          }
        }
      }
    }

    const body = cb.body
    if (body && body.text != null) {
      session.bodyText = WhatsappUtils.safeStr(body.text)
    }

    const footer = cb.footer
    if (footer && footer.text != null) {
      session.footerText = WhatsappUtils.safeStr(footer.text).trim()
    }

    const interactiveType = WhatsappUtils.safeStr(cb.type).trim().toLowerCase()
    const action = cb.action || {}

    if (interactiveType === 'button' && Array.isArray(action.buttons)) {
      const replyButtons = action.buttons.filter(b => b && WhatsappUtils.upper(b.type) === 'REPLY' && b.reply)
      for (let i = 0; i < Math.min(3, replyButtons.length); i++) {
        const n = i + 1
        const reply = replyButtons[i].reply
        session[`button${n}Type`] = 'QUICK_REPLY'
        session[`button${n}Text`] = WhatsappUtils.safeStr(reply.title).trim() || `Button ${n}`
      }
    } else if (interactiveType === 'list') {
      session.button1Type = 'LIST'
      session.button1Text = WhatsappUtils.safeStr(action.button).trim() || 'Menu'
      const sections = Array.isArray(action.sections) ? action.sections : []
      const section = sections[0]
      if (section) {
        session.sectionTitle = WhatsappUtils.safeStr(section.title).trim() || 'Options'
        const rows = Array.isArray(section.rows) ? section.rows : []
        for (let i = 0; i < Math.min(10, rows.length); i++) {
          const row = rows[i] || {}
          const n = i + 1
          session[`row${n}Id`] = WhatsappUtils.safeStr(row.id).trim()
          session[`row${n}Title`] = WhatsappUtils.safeStr(row.title).trim() || `Row ${n}`
          session[`row${n}Description`] = WhatsappUtils.safeStr(row.description).trim()
        }
      }
    } else if (interactiveType === 'cta_url') {
      const params = action.parameters || {}
      session.button1Type = 'URL'
      session.button1Text = WhatsappUtils.safeStr(params.display_text).trim() || 'Visit'
      session.button1Url = WhatsappUtils.safeStr(params.url).trim()
    } else if (interactiveType === 'flow') {
      const params = action.parameters || {}
      session.button1Type = 'FLOW'
      session.button1Text = WhatsappUtils.safeStr(params.flow_cta).trim() || 'Flow'
    }

    return session
  }
}

export function buildWaNumberLabel(phone, waNumberDetails) {
  const title = waNumberDetails && waNumberDetails[phone] && waNumberDetails[phone].title
  if (title) return `<${title}> (${phone})`
  return phone
}

export function getWaOrgAccountNumbers(waOrgAccounts) {
  if (!waOrgAccounts) return { fromNumbers: [], waNumberDetails: undefined }
  const fromNumbers = waOrgAccounts.waNumbers || []
  const waNumberDetails = waOrgAccounts.waNumberDetails || undefined
  return { fromNumbers, waNumberDetails }
}

export function buildWaNumberSelectOptions(fromNumbers, waNumberDetails) {
  return (fromNumbers || []).map(phone => ({
    key: phone,
    label: buildWaNumberLabel(phone, waNumberDetails),
  }))
}

/** Digits-only compare key for WA / CRM phone matching. */
export function normalizeWaPhoneForCompare(phone) {
  return String(phone || '').replace(/\D/g, '')
}

export function extractCrmNumbersItems(crmnumbersResult) {
  if (!crmnumbersResult) return []
  if (Array.isArray(crmnumbersResult)) return crmnumbersResult
  if (Array.isArray(crmnumbersResult.items)) return crmnumbersResult.items
  return []
}

function crmNumberHasWhatsAppProvider(crmNumber) {
  return (crmNumber && crmNumber.providers || []).some(p => String(p).toUpperCase() === 'WHATSAPP')
}

/**
 * If a WhatsApp-onboarded CRM number matches fromNumber (normalized) and has
 * waVendorProjectId, return that id; otherwise ''.
 */
export function resolveVendorProjectIdForFromNumber(fromNumber, crmNumbers) {
  const normalizedFrom = normalizeWaPhoneForCompare(fromNumber)
  if (!normalizedFrom || !Array.isArray(crmNumbers) || crmNumbers.length === 0) return ''
  const match = crmNumbers.find((item) => {
    if (!item || !crmNumberHasWhatsAppProvider(item)) return false
    if (normalizeWaPhoneForCompare(item.phone) !== normalizedFrom) return false
    return item.waVendorProjectId != null && String(item.waVendorProjectId).trim() !== ''
  })
  return match ? String(match.waVendorProjectId).trim() : ''
}

/** Adds vendorProjectId to queryParams when resolvable for fromNumber. */
export function withVendorProjectIdQueryParam(queryParams, fromNumber, crmNumbers) {
  const vendorProjectId = resolveVendorProjectIdForFromNumber(fromNumber, crmNumbers)
  if (!vendorProjectId) return queryParams
  return { ...queryParams, vendorProjectId }
}

export const WA_CONTENT_ITYPE_PREFIX = 'WACONTENT__'
export const WA_CONTENT_TYPE_TEMPLATE = 'TEMPLATE_INSTANCE'
export const WA_CONTENT_TYPE_SESSION = 'SESSION'

export function getWaContentIdFromIType(iType) {
  const key = String(iType || '')
  if (!key.startsWith(WA_CONTENT_ITYPE_PREFIX)) return null
  return key.slice(WA_CONTENT_ITYPE_PREFIX.length) || null
}

export function formatWaContentTypeOptionLabel(iType) {
  const t = String(iType || '').toUpperCase()
  if (t === WA_CONTENT_TYPE_TEMPLATE) return 'TEMPLATE'
  if (t === WA_CONTENT_TYPE_SESSION) return 'SESSION'
  return t || '—'
}

export function getWaTemplateFromContent(content) {
  const t = content && content.waTemplate
  return t && typeof t === 'object' ? t : null
}

export function isWaContentSendable(content, { agentWABA, isSessionActive } = {}) {
  if (!content || String(content.state || '').toUpperCase() !== 'LIVE') return false
  const iType = String(content.iType || '').toUpperCase()
  if (iType === WA_CONTENT_TYPE_TEMPLATE) {
    return !!getWaTemplateFromContent(content)
  }
  if (iType === WA_CONTENT_TYPE_SESSION) {
    return !!isSessionActive
  }
  return false
}

export function buildSendableWaContentTypeOptions(contents, { agentWABA, isSessionActive } = {}) {
  return (contents || [])
    .filter(c => isWaContentSendable(c, { agentWABA, isSessionActive }))
    .map(c => ({
      key: `${WA_CONTENT_ITYPE_PREFIX}${c.id}`,
      label: `${c.title || c.id} (${formatWaContentTypeOptionLabel(c.iType)})`,
    }))
}

export function resolveWaTemplateDisplayName(t) {
  return String((t && (t.name || t.templateName || t.title)) || '').trim()
}

export function resolveWaTemplateCategory(t) {
  return String((t && (t.category || t.templateCategory)) || '').trim().toUpperCase()
}

export function getTemplateCategoryByName(templates) {
  const map = {}
  const list = templates || []
  list.forEach((t) => {
    const name = resolveWaTemplateDisplayName(t)
    if (!name) return
    const category = resolveWaTemplateCategory(t)
    if (!category) return
    map[name] = category
    map[name.toLowerCase()] = category
  })
  return map
}

function toWaSendContentOption(content) {
  const title = content.title || content.id
  return {
    key: content.id,
    label: `${title} (${formatWaContentTypeOptionLabel(content.iType)})`,
  }
}

export function buildWaSendPickerContentOptions(contents, { agentWABA, isSessionActive } = {}) {
  const session = []
  const marketing = []
  const utility = []
  const list = contents || []
  list.forEach((c) => {
    if (!isWaContentSendable(c, { agentWABA, isSessionActive })) return
    const iType = String(c.iType || '').toUpperCase()
    if (iType === WA_CONTENT_TYPE_SESSION) {
      session.push(toWaSendContentOption(c))
      return
    }
    if (iType !== WA_CONTENT_TYPE_TEMPLATE) return
    const category = resolveWaTemplateCategory(getWaTemplateFromContent(c))
    if (category === 'MARKETING') marketing.push(toWaSendContentOption(c))
    else if (category === 'UTILITY') utility.push(toWaSendContentOption(c))
  })
  return { session, marketing, utility }
}

function toPrefixedWaContentOption(opt) {
  return {
    key: `${WA_CONTENT_ITYPE_PREFIX}${opt.key}`,
    label: opt.label || opt.key,
  }
}

export function buildWaSendUnifiedMessageOptions({ contentOptions } = {}) {
  const options = []
  const marketingKeys = []
  const contentKeys = []
  const buckets = [
    { list: (contentOptions && contentOptions.marketing) || [], typeLabel: 'MARKETING' },
    { list: (contentOptions && contentOptions.utility) || [], typeLabel: 'UTILITY' },
  ]
  buckets.forEach(({ list, typeLabel }) => {
    list.forEach((opt) => {
      if (!opt || !opt.key) return
      const prefixed = toPrefixedWaContentOption(opt)
      options.push(prefixed)
      contentKeys.push(prefixed.key)
      if (typeLabel === 'MARKETING') marketingKeys.push(prefixed.key)
    })
  })
  return { options, marketingKeys, contentKeys }
}
